import { LOCATION_MODE } from '@/types/location-mode';
import { PRICE_DISPLAY_MODE } from '@/types/price-display-mode';
import { QUOTA_PERIOD } from '@/types/quota-period';
import { RECURRENCE_INTERVAL } from '@/types/recurrence-interval';
import { RECURRING_BOOKING_MODE } from '@/types/recurring-booking-mode';
import { z } from 'zod';

/**
 * Um único schema para criar e editar serviço — as duas telas mandam o mesmo
 * payload, então não podem divergir. Os campos `has*` são só do formulário
 * (ligam/desligam blocos); a conversão para o corpo da API mora em
 * `hooks/use-services.ts`.
 */
export const serviceSchema = z
  .object({
    name: z.string().min(1, 'Por favor, preencha o nome do serviço.'),
    description: z.string().optional(),
    isActive: z.boolean().optional(),

    hasFixedPrice: z.boolean().optional(),
    defaultPrice: z.string().optional(),
    priceDisplayMode: z.nativeEnum(PRICE_DISPLAY_MODE).optional(),
    allowedPaymentMethods: z
      .array(z.string())
      .nonempty({ message: 'Selecione pelo menos um método de pagamento.' }),

    /** Onde acontece; só `PROVIDER_SPACE` pede endereço. */
    locationMode: z.nativeEnum(LOCATION_MODE).optional(),
    address: z.string().optional(),

    /**
     * Só do formulário: quem marcou que o serviço entra na agenda tem de dizer
     * quanto dura. Ausente (telas que não perguntam), a duração segue opcional.
     */
    isSchedulable: z.boolean().optional(),
    /** Nulo = serviço não agendável online. */
    durationMinutes: z.number().int().positive().nullable().optional(),
    slotCapacity: z.number().int().positive().optional(),

    /** Assinatura: o cliente paga o ciclo e marca dentro da cota. */
    isSubscription: z.boolean().optional(),
    subscriptionQuota: z.number().int().positive().optional(),
    subscriptionQuotaPeriod: z.nativeEnum(QUOTA_PERIOD).optional(),
    recurringBookingMode: z.nativeEnum(RECURRING_BOOKING_MODE).optional(),
    recurringBillingInterval: z.nativeEnum(RECURRENCE_INTERVAL).optional(),
    recurringHorizonDays: z.number().int().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.locationMode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Escolha onde o serviço acontece.',
        path: ['locationMode'],
      });
    }
    if (data.locationMode === LOCATION_MODE.PROVIDER_SPACE && !data.address?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'O endereço deve ser preenchido.',
        path: ['address'],
      });
    }
    if (data.hasFixedPrice && !data.defaultPrice?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'O preço deve ser preenchido.',
        path: ['defaultPrice'],
      });
    }
    // Recorrência sem duração não vira horário nenhum — o backend recusa.
    if (
      (data.isSubscription ||
        (data.recurringBookingMode && data.recurringBookingMode !== RECURRING_BOOKING_MODE.OFF)) &&
      !data.durationMinutes
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Defina a duração do atendimento para aceitar recorrência.',
        path: ['durationMinutes'],
      });
    }
    // Assinatura sem número de sessões não sabe o que entrega — na hora de
    // marcar não haveria contra o que comparar o saldo do cliente.
    if (data.isSubscription && !data.subscriptionQuota) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Diga quantas sessões por período a assinatura dá direito.',
        path: ['subscriptionQuota'],
      });
    }
    if (data.isSubscription && !data.subscriptionQuotaPeriod) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Escolha se a cota renova por semana ou por mês.',
        path: ['subscriptionQuotaPeriod'],
      });
    }
    // Dizer que o serviço entra na agenda e não dizer quanto dura deixa a grade
    // sem tamanho de bloco.
    if (data.isSchedulable === true && !data.durationMinutes) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Diga quanto dura o atendimento.',
        path: ['durationMinutes'],
      });
    }
  });

export type ServiceFormData = z.infer<typeof serviceSchema>;

export const emptyServiceForm: ServiceFormData = {
  name: '',
  description: '',
  isActive: true,
  hasFixedPrice: false,
  defaultPrice: '',
  priceDisplayMode: PRICE_DISPLAY_MODE.SHOW,
  allowedPaymentMethods: ['PIX', 'CASH'],
  locationMode: undefined,
  address: '',
  isSchedulable: true,
  durationMinutes: null,
  slotCapacity: 1,
  isSubscription: false,
  subscriptionQuota: 4,
  subscriptionQuotaPeriod: QUOTA_PERIOD.MONTH,
  recurringBookingMode: RECURRING_BOOKING_MODE.OFF,
  recurringBillingInterval: RECURRENCE_INTERVAL.MONTHLY,
  recurringHorizonDays: 30,
};
